import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, ArrowRight, Compass, Lock, BookOpen, Heart, MessageCircle, Gift, Users } from "lucide-react";
import IntentPicker from "../components/IntentPicker";

const NAVY = "#0d2340";
const NAVY_DARK = "#0A1B33";
const GOLD = "#E0A73A";
const BG = "/assets/journey-bg.jpg";
const hero = { color: "#ffffff", textShadow: "0 2px 20px rgba(0,0,0,0.65)" };

const GoldSwoosh = ({ className = "w-44 h-3" }) => (
  <svg viewBox="0 0 200 12" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8 Q100 0 197 8" stroke={GOLD} strokeWidth="3.5" strokeLinecap="round" />
  </svg>
);

function Particles() {
  const bits = useMemo(() => Array.from({ length: 10 }).map((_, i) => ({
    left: 6 + (i * 9.3) % 90, delay: (i * 1.6) % 10, dur: 9 + (i % 5) * 2.2, size: 3 + (i % 3),
  })), []);
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {bits.map((b, i) => (
        <span key={i} className="wiz-particle" style={{ left: `${b.left}%`, width: b.size, height: b.size, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s` }} />
      ))}
    </div>
  );
}

// Full-bleed sunlit background + breathing horizon sun glow + gold dust + centered content
function Shell({ children }) {
  return (
    <div className="relative w-full min-h-[100dvh] overflow-hidden" style={{ background: NAVY }}>
      <img src={BG} alt="" className="fixed inset-0 w-full h-full object-cover" style={{ objectPosition: "center 45%", filter: "saturate(1.15) brightness(1.06) contrast(1.02)" }} />
      <div className="fixed inset-0" style={{ background: "linear-gradient(180deg, rgba(13,35,64,0.50) 0%, rgba(13,35,64,0.12) 26%, rgba(13,35,64,0.06) 50%, rgba(10,27,51,0.78) 100%)" }} />
      <div className="sun-glow fixed left-1/2 top-[47%] pointer-events-none" style={{ width: 440, height: 440, borderRadius: "9999px", transform: "translate(-50%,-50%)", background: "radial-gradient(circle, rgba(255,220,150,0.6), rgba(255,185,95,0.22) 42%, rgba(255,185,95,0) 70%)", filter: "blur(16px)" }} />
      <Particles />
      <div className="relative z-10 min-h-[100dvh] w-full flex justify-center overflow-y-auto">
        <div className="w-full max-w-md px-6 flex flex-col" style={{ paddingTop: "calc(env(safe-area-inset-top) + 28px)", paddingBottom: "calc(env(safe-area-inset-bottom) + 28px)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function OrgLanding() {
  const { key, orgKey } = useParams();
  const k = key || orgKey;
  const navigate = useNavigate();
  const { verifyCode, refreshMe } = useAuth();
  const [sp] = useSearchParams();
  const previewScreen = { "2": "scripture", "3": "thanks" }[sp.get("screen")];

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [phase, setPhase] = useState("welcome"); // welcome | verify | scripture | thanks
  useEffect(() => { if (previewScreen) setPhase(previewScreen); }, [previewScreen]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [hp, setHp] = useState(""); // honeypot — hidden from humans; bots fill it
  const [formToken, setFormToken] = useState("");
  const formShownAt = useRef(Date.now());

  const fetchFormToken = async () => {
    try { const { data } = await api.get("/public/form-token"); setFormToken(data.token || ""); }
    catch (e) { console.error("Failed to prefetch form token (retried on submit):", e); }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/public/org/${encodeURIComponent(k)}`); setOrg(data); }
      catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
    fetchFormToken();
  }, [k]);

  const sendCode = async () => {
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try { await api.post("/public/org-signin", { key: k, email, name, hp, fs: Date.now() - formShownAt.current, ft: formToken }); setPhase("verify"); toast.success("We emailed you a 6-digit code"); }
    catch (e) { fetchFormToken(); toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const u = await verifyCode(email, code);
      // Only brand-new partners (no intent chosen yet) see the intent + welcome onboarding.
      // Admins/team and already-onboarded partners go straight into the app.
      if (u && u.role === "partner" && !u.intent_set) setPhase("intent");
      else navigate("/app");
    }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }}><Loader2 className="h-8 w-8 animate-spin text-white/70" /></div>;

  if (notFound) return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5 text-center">
      <Compass className="h-12 w-12 text-muted-foreground mb-4" />
      <h1 className="font-heading font-bold text-2xl text-[var(--brand-navy)]">Mission not found</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-sm">We couldn't find a mission at this link. Double-check the invite from your missionary.</p>
      <Link to="/auth" data-testid="orglanding-to-auth" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold accent-text hover:underline">Go to sign in</Link>
    </div>
  );

  // ============ SCREEN 1 — Welcome + sign-in form ============
  if (phase === "welcome" || phase === "verify") {
    return (
      <Shell>
        <div className="flex flex-col items-center text-center flex-1">
          {org?.logo_file_id ? (
            <img src={fileUrl(`/api/files/${org.logo_file_id}`, 200)} alt={org.org_name} className="h-16 w-16 rounded-2xl object-cover shadow-lg" />
          ) : (
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-2xl shadow-lg" style={{ background: GOLD, color: NAVY_DARK }}>{(org?.org_name || "M")[0]}</div>
          )}
          <div className="text-[12px] uppercase tracking-[0.22em] font-bold mt-3" style={hero}>{org?.org_name}</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-[1.02] mt-3" style={hero} data-testid="org-landing-name">Welcome<br />to the Journey.</h1>
          <div className="my-4"><GoldSwoosh /></div>
          <p className="text-[17px] font-medium max-w-xs" style={hero}>You are one of the people helping make this mission possible.</p>

          <div className="grid grid-cols-3 w-full mt-7 mb-6">
            <Trio icon={Heart} label="Every prayer." />
            <Trio icon={MessageCircle} label="Every encouragement." divider />
            <Trio icon={Gift} label="Every gift." divider />
          </div>

          <div className="flex items-center gap-3 rounded-2xl px-4 py-3 w-full" style={{ background: "rgba(10,27,51,0.5)", backdropFilter: "blur(10px)", border: `1px solid ${GOLD}33` }}>
            <Users className="h-6 w-6 shrink-0" style={{ color: GOLD }} />
            <p className="text-sm text-left font-medium leading-snug text-white">Together we become part of the story God is writing.</p>
          </div>
        </div>

        {/* Sign-in form pinned to bottom */}
        <div className="mt-8">
          <div className="rounded-3xl p-5 shadow-[0_18px_55px_rgba(0,0,0,0.5)]" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)" }} data-testid="org-landing-card">
            {phase === "welcome" ? (
              <div className="space-y-3.5">
                <input
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <div>
                  <Label className="text-sm text-[#2C2C3E]">Your name</Label>
                  <Input data-testid="org-landing-name-input" className="mt-1.5 rounded-xl h-12" value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your name" />
                </div>
                <div>
                  <Label className="text-sm text-[#2C2C3E]">Email address</Label>
                  <Input data-testid="org-landing-email-input" type="email" className="mt-1.5 rounded-xl h-12" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                </div>
                <Button data-testid="org-landing-continue-btn" disabled={busy} onClick={sendCode} className="w-full rounded-full text-white h-12 hover:opacity-90" style={{ background: NAVY }}>
                  {busy ? "Please wait…" : <span className="flex items-center justify-center gap-2">Continue <ArrowRight className="h-4 w-4" /></span>}
                </Button>
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5"><Lock className="h-3 w-3" /> We'll email you a secure sign-in code.</p>
              </div>
            ) : (
              <div className="space-y-3.5">
                <button type="button" onClick={() => { setPhase("welcome"); setCode(""); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[var(--brand-navy)]"><ArrowLeft className="h-3.5 w-3.5" /> Use a different email</button>
                <div className="text-sm text-[#2C2C3E]">Enter the 6-digit code sent to <strong>{email}</strong></div>
                <Input data-testid="org-landing-code-input" inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="rounded-xl h-14 text-center text-2xl tracking-[0.4em] font-bold" placeholder="000000" />
                <button type="button" data-testid="org-landing-resend" onClick={sendCode} className="text-xs accent-text hover:underline">Resend code</button>
                <Button data-testid="org-landing-verify-btn" disabled={busy} onClick={verify} className="w-full rounded-full h-12 hover:opacity-90 font-semibold" style={{ background: GOLD, color: "#0d2340" }}>
                  {busy ? "Verifying…" : "Verify & continue"}
                </Button>
              </div>
            )}
          </div>
          <div className="mt-4 text-center">
            <Link to="/auth" data-testid="org-landing-main-signin" className="text-xs text-white/85 hover:text-white drop-shadow">Already connected to another mission? <span className="font-semibold" style={{ color: GOLD }}>Sign in normally →</span></Link>
          </div>
        </div>
      </Shell>
    );
  }

  // ============ SCREEN — Intent: how would you like to walk with us? ============
  if (phase === "intent") {
    return (
      <Shell>
        <div className="flex flex-col flex-1">
          <div className="text-center" style={{ paddingTop: "3vh" }}>
            <Users className="h-9 w-9 mx-auto" style={{ color: GOLD }} />
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl leading-tight mt-4" style={hero}>How would you like<br />to walk with us?</h1>
            <div className="flex justify-center my-4"><GoldSwoosh className="w-24 h-2.5" /></div>
            <p className="text-[15px] font-medium max-w-xs mx-auto" style={hero}>Choose what feels right — you can always change this later. Pick one or more.</p>
          </div>
          <div className="mt-auto pt-6">
            <div className="rounded-3xl p-4 shadow-[0_18px_55px_rgba(0,0,0,0.5)]" style={{ background: "rgba(255,255,255,0.94)", backdropFilter: "blur(10px)" }}>
              <IntentPicker onDone={async () => { await refreshMe(); setPhase("scripture"); }} gold={GOLD} />
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ============ SCREEN 2 — Scripture (after sign-in) ============
  if (phase === "scripture") {
    return (
      <Shell>
        <div className="flex flex-col flex-1">
          <div className="text-center" style={{ paddingTop: "2vh" }}>
            <BookOpen className="h-9 w-9 mx-auto" style={{ color: GOLD }} />
            <p className="font-heading font-extrabold text-3xl sm:text-4xl leading-tight mt-6" style={hero}>"How can they hear unless someone tells them?</p>
            <div className="flex justify-center my-5"><GoldSwoosh className="w-24 h-2.5" /></div>
            <p className="font-heading font-extrabold text-3xl sm:text-4xl leading-tight" style={hero}>And how can anyone go unless someone is sent?"</p>
            <div className="text-sm font-bold tracking-[0.15em] mt-6" style={{ color: GOLD, textShadow: "0 1px 10px rgba(0,0,0,0.5)" }}>ROMANS 10:14-15</div>
          </div>
          <div className="mt-auto">
            <p className="text-center text-white text-[17px] font-semibold leading-relaxed mb-6" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}>God's mission moves forward<br />when people say yes —<br />both to go and to send.</p>
            <Button data-testid="onboarding-next-btn" onClick={() => setPhase("thanks")} className="w-full rounded-full text-white h-14 hover:opacity-90" style={{ background: NAVY }}>
              <span className="flex items-center justify-center gap-2">Continue <ArrowRight className="h-4 w-4" /></span>
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // ============ SCREEN 3 — Thank you (after sign-in) → Home ============
  return (
    <Shell>
      <div className="flex-1 flex flex-col">
        <div className="text-center" style={{ paddingTop: "9vh" }}>
          <Heart className="h-9 w-9 mx-auto" style={{ color: GOLD }} />
          <h1 className="font-heading font-extrabold text-5xl mt-3" style={hero}>Thank you.</h1>
          <p className="text-[17px] font-medium mt-3 max-w-xs mx-auto leading-snug" style={hero}>This journey is possible because people like you choose to send.</p>
          <div className="flex justify-center my-5"><GoldSwoosh className="w-28 h-2.5" /></div>
        </div>
        <div className="mt-auto space-y-5 pt-4">
          <Pair iconEl={<i className="fa-solid fa-hands-praying" style={{ color: GOLD, fontSize: 22 }} />} a="You pray." b="We go." />
          <Pair iconEl={<Heart className="h-6 w-6" style={{ color: GOLD }} />} a="You encourage." b="We persevere." />
          <Pair iconEl={<Gift className="h-6 w-6" style={{ color: GOLD }} />} a="You give." b="Lives are changed." />
          <Pair iconEl={<Users className="h-6 w-6" style={{ color: GOLD }} />} a="Together," b="we fulfill the mission." />
        </div>
      </div>
      <div className="pt-8">
        <Button data-testid="onboarding-begin-btn" onClick={() => navigate("/app")} className="w-full rounded-full h-14 hover:opacity-90 text-base font-bold" style={{ background: GOLD, color: "#0d2340" }}>
          <span className="flex items-center justify-center gap-2">Begin the Journey <ArrowRight className="h-5 w-5" /></span>
        </Button>
      </div>
    </Shell>
  );
}

function Trio({ icon: Icon, label, divider }) {
  return (
    <div className={`flex flex-col items-center gap-2 px-1 ${divider ? "border-l" : ""}`} style={divider ? { borderColor: "rgba(255,255,255,0.28)" } : {}}>
      <Icon className="h-7 w-7" style={{ color: GOLD }} />
      <span className="text-[12px] font-semibold leading-tight text-white" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}>{label}</span>
    </div>
  );
}

function Pair({ iconEl, a, b }) {
  return (
    <div className="flex items-center gap-4">
      <div className="h-12 w-12 rounded-full border-2 flex items-center justify-center shrink-0" style={{ borderColor: GOLD }}>
        {iconEl}
      </div>
      <div className="text-[17px] text-white drop-shadow leading-tight">
        <div className="font-bold">{a}</div>
        <div className="text-white/90">{b}</div>
      </div>
    </div>
  );
}
